import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { Subscription } from 'rxjs/Subscription';
import * as _ from 'lodash';
import * as stringify from 'json-stable-stringify';
import { RtbPartnerService } from 'app/shared/services/rtb-partner.service';
import { SharedService } from 'app/shared/services/shared.service';
import { DictionaryService } from 'app/shared/services/dictionary.service';
import { UserService } from 'app/shared/services/user.service';
import { HttpService } from 'app/shared/services/http.service';
import { Observable } from 'rxjs/Observable';
import { FIELDS, RTB_PROVIDERS, WEB_ENV, APP_ENV } from './rtb-partners.constants';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';

@AutoUnsubscribe()
@Component({
  selector: 'cedato-new-rtb-partner',
  templateUrl: './new-rtb-partner.component.html',
  styleUrls: ['./new-rtb-partner.component.scss'],
})
export class NewRtbPartnerComponent implements OnInit {
  public environments = [
    { id: WEB_ENV, name: 'web' },
    { id: APP_ENV, name: 'inApp' },
  ];
  public providerFields = this.fb.array([]);
  public rtbProviders: any[] = [...RTB_PROVIDERS];
  public rtbPartner: any;
  public allSections: Array<any>;
  public queryParams: any;
  public editMode: boolean;
  public subscriptions: Array<Subscription>;
  public routeParams: any;
  public activeSection: number;
  public errorInputs: Array<string>;
  public isLoading: boolean;
  public backLink: string;
  public rtbPartnerEndpointAdvancedMode: boolean;

  constructor(
    public rtbPartnerService: RtbPartnerService,
    public route: ActivatedRoute,
    public dictionaryService: DictionaryService,
    public httpService: HttpService,
    public sharedService: SharedService,
    public router: Router,
    public userService: UserService,
    private fb: FormBuilder,
  ) {
    this.rtbPartner = {};
    this.allSections = [
      {
        name: 'RTB_ENDPOINTS',
        value: 'rtbPartnerSettings',
        toggleStatus: true,
      },
      {
        name: 'JSON_CONFIG',
        value: 'jsonConfig',
        toggleStatus: true,
        isAdminOnly: true,
        onIf: false,
        iconNameIfOn: 'file',
        iconTooltip: 'JSON Config were added',
      },
    ];
    this.editMode = false;
    this.subscriptions = [];
    this.queryParams = {};
    this.routeParams = {};
    this.activeSection = 0;
    this.errorInputs = [];
    this.isLoading = false;
    this.backLink = '/rtb-partners/manage';
    this.rtbPartnerEndpointAdvancedMode = false;
  }

  ngOnInit(): void {
    if (this.userService.getFeatureState('rtb_demand')) {
      this.initialize();

      this.subscriptions.push(
        this.sharedService.getMessagesBus().subscribe((response: any) => {
          if (response.type === 'NETWORK_CHANGE') {
            this.errorInputs = [];
            if (!this.editMode) {
              this.isLoading = true;
              this.initialize();
            } else {
              this.router.navigate([this.router.url]).then(() => {
                return;
              });
            }
          }
        }),
      );

      this.subscriptions.push(
        this.route.params.subscribe((params: any) => {
          if (params.id > 0) {
            this.routeParams.id = params.id;
            this.editMode = true;
            this.sharedService.setPageTitle('EDIT_CAMPAIGN');
            this.parsePartnerData();
          } else {
            this.sharedService.setPageTitle('NEW_CAMPAIGN');
            this.initialize();
          }
        }),
      );

      this.subscriptions.push(
        this.providerFields.valueChanges.pipe(debounceTime(500)).subscribe((value) => {
          this.updateConfig(value);
        }),
      );
    } else {
      this.router.navigate(['/']).then(() => {
        return;
      });
    }
  }

  ngOnDestroy(): void {
  }

  detectProvider(): void {
    const config = this.parseConfigString(this.rtbPartner.config_json);
    this.rtbPartner.provider = 0; // Default is "Other" if no provider is found

    if (!config) {
      this.buildForm();
      return;
    }

    for (const provider of this.rtbProviders) {
      // Check for matching bidderKey or bidderType in both web and inApp configurations
      const matchesBidderKey = config['bidderKey'] && (
        (provider.web && provider.web.config && provider.web.config.bidderKey === config['bidderKey']) ||
        (provider.inApp && provider.inApp.config && provider.inApp.config.bidderKey === config['bidderKey'])
      );
      const matchesBidderType = config['bidderType'] && (
        (provider.web && provider.web.config && provider.web.config.bidderType === config['bidderType']) ||
        (provider.inApp && provider.inApp.config && provider.inApp.config.bidderType === config['bidderType'])
      );

      // Check for matching endpoint
      const matchesEndpoint = provider.endpointMatcher && [this.rtbPartner.endpoint, this.rtbPartner.us_east_endpoint, this.rtbPartner.us_west_endpoint].some(endpoint => endpoint && endpoint.includes(provider.endpointMatcher));

      if (matchesBidderKey || matchesBidderType || matchesEndpoint) {
        this.rtbPartner.provider = provider.id;
        this.detectEnvironment(provider, config);
        break;
      }
    }

    this.buildForm();
  }

  detectEnvironment(provider, config) {
    if (config.environment) {
      this.rtbPartner.environment = config.environment;
      return;
    }

    // Unique fields for web and inApp configurations
    const webUniqueFields = ['cookie_sync_id', 'siteId', 'syncUrl', 'publisherId'];
    const inAppUniqueFields = ['appId', 'appBundle', 'tagid', 'schainStr'];

    let webFieldCount = 0;
    let inAppFieldCount = 0;

    // Count how many unique fields are present in the config for each environment
    Object.keys(config).forEach(key => {
      if (webUniqueFields.includes(key)) {
        webFieldCount++;
      }
      if (inAppUniqueFields.includes(key)) {
        inAppFieldCount++;
      }
    });

    if (webFieldCount >= inAppFieldCount) {
      this.rtbPartner.environment = WEB_ENV; // Web
    } else {
      this.rtbPartner.environment = APP_ENV; // InApp
    }
  }

  updateConfig(controls) {
    const config_json = this.parseConfigString(this.rtbPartner.config_json || '{}');
    const obj = controls.reduce((result, next) => {
      let value = next.prefix ? `${next.prefix}${next.value}` : next.value;
      if (next.name === 'authorization' && next.value) {
        const trimmedValue = next.value.trim();
        if (!trimmedValue.toLowerCase().startsWith('basic ')) {
          value = `Basic ${trimmedValue}`;
        } else {
          value = trimmedValue;
        }
      }

      if (!next.ignoreAssignment) {
        if (next.path) {
          _.setWith(result, next.path, value, Object);
        } else {
          result[next.name] = value;
        }
      }

      return result;
    }, {});

    controls.forEach((control: any) => {
      if (control.replaceIn) {
        const { value, path, param, pathAttribute } = control.replaceIn;

        const newParamValue = control.value || '';
        if (newParamValue.length > 0 || newParamValue === '') {
          if (value && path) {
            _.setWith(obj, path, value.replace(`[${control.name}]`, newParamValue), Object);
          }

          if (path === 'endpoint' && param) {
            const url = new URL(this.rtbPartner[path]);

            if (control.required) {
              url.searchParams.set(param, control.value);
            } else {
              if (control.value === '') {
                url.searchParams.delete(param);
              } else {
                url.searchParams.set(param, control.value);
              }
            }

            this.rtbPartner[path] = url.toString();
          }

          if (path === 'endpoint' && pathAttribute) {
            if (control.value !== '') {
              if (this.rtbPartner.originalEndpoint === undefined) {
                this.rtbPartner.originalEndpoint = this.rtbPartner[path];
              }
              this.rtbPartner[path] = this.rtbPartner[path].replace(pathAttribute, control.value);
            } else if (this.rtbPartner.originalEndpoint) {
              this.rtbPartner[path] = this.rtbPartner.originalEndpoint;
            }
          }
        }
      }
    });

    this.updateConfigJSON(stringify(_.merge(config_json, obj)));
  }

  getInvalidProviderFields(): string[] {
    const fields = [];

    this.providerFields.controls.forEach((group: FormGroup) => {
      if (!group.valid) {
        fields.push(group.get('label').value);
      }
    });

    return fields;
  }

  providerChange() {
    this.rtbPartner.config_json = '{}';
    this.rtbPartner.environment = undefined;

    this.buildForm(true);
  }

  toggleEnvironment(id: number) {
    this.rtbPartner.environment = id;

    this.buildForm(true);
  }

  getEnvNameFromId(id: number): string {
    const env = this.environments.find((e) => e.id === id);

    if (env) {
      return env.name;
    }

    return '';
  }

  getProviderConfig(id) {
    return this.rtbProviders.find((p) => p.id === id);
  }

  isEnvAvailable(id: number): boolean {
    const provider: any = this.getProviderConfig(this.rtbPartner.provider);

    if (!this.sharedService.isEmpty(provider)) {
      const env = this.getEnvNameFromId(id);

      return !this.sharedService.isEmpty(provider[env]) && provider[env].disabled !== true;
    }
  }

  buildForm(forceConfigUpdate = false) {
    this.cleanForm();

    const providerExists = this.rtbPartner.provider !== undefined && this.rtbPartner.provider !== null;
    // Reset config_json if provider was removed
    if (!providerExists) {
      return;
    }

    const provider: any = this.getProviderConfig(this.rtbPartner.provider);
    const env = this.getEnvNameFromId(this.rtbPartner.environment);
    this.rtbPartner.endpointReadonly = !!provider.endpointReadonly;

    if (env) {
      const { available, required, config, endpoint, replaceEndpoint } = (provider[env] || {}) as any;
      let config_json = {};

      config.environment = this.rtbPartner.environment;

      if (endpoint !== '' && replaceEndpoint && !this.editMode) {
        this.rtbPartner.endpoint = endpoint;
      }

      if (this.rtbPartner.config_json && !forceConfigUpdate) {
        config_json = this.parseConfigString(this.rtbPartner.config_json || '{}');
      } else if (config) {
        this.updateConfigJSON(stringify(config));
        config_json = this.parseConfigString(this.rtbPartner.config_json || '{}');
      }

      if (available) {
        available.forEach((availableField: any) => {
          const isObj = typeof availableField !== 'string';
          const fieldName = isObj ? availableField.name : availableField;
          const field = FIELDS.find((f) => f.name === fieldName);
          let shouldAdd = true;

          if (isObj && availableField.permissions) {
            shouldAdd = false;
          }

          if (field && shouldAdd) {
            const isRequired = (required || []).includes(fieldName);
            let formValue = config_json[fieldName];

            if (isObj && availableField.path) {
              formValue = _.get(config_json, availableField.path);
            }

            if (
              isObj &&
              availableField.readFrom &&
              availableField.readFrom.path &&
              (availableField.readFrom.pattern || availableField.readFrom.param)
            ) {
              let extractedValue: string | null;
              const target = _.get(config_json, availableField.readFrom.path, '');

              if (availableField.readFrom.path === 'endpoint') {
                extractedValue = availableField.readFrom.pattern
                  ? this.regExpReader(this.rtbPartner.endpoint, availableField.readFrom.pattern)
                  : this.queryParamsReader(this.rtbPartner.endpoint, availableField.readFrom.param);
              } else {
                extractedValue = this.regExpReader(target, availableField.readFrom.pattern);
              }

              if (extractedValue != null) {
                formValue = extractedValue;
              }
            }

            if (isObj && availableField.prefix) {
              formValue = formValue.replace(availableField.prefix, '');
            }

            const groupData: any = {
              required: this.fb.control(isRequired),
              label: this.fb.control(field.label),
              name: this.fb.control(field.name),
              type: this.fb.control(availableField.type || field.type),
              value: this.fb.control(this.routeParams.id ? formValue : '', isRequired ? Validators.required : []),
            };

            if (isObj && availableField.path) {
              groupData.path = this.fb.control(availableField.path);
            }

            if (isObj && availableField.prefix) {
              groupData.prefix = this.fb.control(availableField.prefix);
            }

            if (isObj && availableField.replaceIn) {
              groupData.replaceIn = this.fb.control(availableField.replaceIn);
            }

            if (isObj && availableField.ignoreAssignment) {
              groupData.ignoreAssignment = this.fb.control(availableField.ignoreAssignment);
            }

            if (isObj && availableField.options) {
              groupData.options = this.fb.control(availableField.options);
            }

            const group = this.fb.group(groupData);

            this.providerFields.push(group);
          }
        });
      }
    }
  }

  updateForm() {
    if (this.rtbPartner.provider && this.rtbPartner.config_json) {
      const provider: any = this.getProviderConfig(this.rtbPartner.provider);

      if (provider) {
        const env: any = this.getEnvNameFromId(this.rtbPartner.environment);

        if (env) {
          const { available } = (provider[env] || {}) as any;
          const config_json = this.parseConfigString(this.rtbPartner.config_jso);

          this.providerFields.controls.forEach((group) => {
            const groupName = group.get('name').value;
            let value = _.get(config_json, group.get('path') ? group.get('path').value : groupName);

            if (value) {
              const field = available.find(({ name }) => name === groupName);

              if (field && field.prefix) {
                value = value.replace(field.prefix, '');
              }

              group.get('value').setValue(value, { emitEvent: false });
            }
          });
        }
      }
    }
  }

  parseConfigString(config: string): object {
    try {
      const parsed = JSON.parse(config || '{}');

      if (!parsed.environment && this.rtbPartner.environment) {
        parsed.environment = this.rtbPartner.environment;
      }

      return parsed;
    } catch (e) {
      console.error(e);
    }
  }

  cleanForm() {
    while (this.providerFields.length !== 0) {
      this.providerFields.removeAt(0);
    }
  }

  initialize(): void {
    this.rtbPartner = _.cloneDeep(this.rtbPartnerService.defaultRTBPartner);
  }

  parsePartnerData(): void {
    this.isLoading = true;

    this.getPartner().subscribe(
      (response) => {
        if (response && response.networkData && response.networkData.networkChanged) {
          this.userService.changeNetworkIfNeeded(response);
        }

        if (response && response.data && response.data.id) {
          this.preparePartnerObject(response.data);
        } else {
          this.onError(true);
        }
        this.isLoading = false;
      },
      (error: any) => {
        this.onError(false);
        this.isLoading = false;
      },
    );
  }

  expandEndpointOptions(): void {
    this.rtbPartnerEndpointAdvancedMode = true;
    if (this.rtbPartner.endpoint && this.rtbPartner.endpoint.toLowerCase().includes('sonobi')) {
      this.rtbPartner.us_west_endpoint = this.rtbPartner.endpoint.replace('east', 'west');
    }
    this.rtbPartner.us_east_endpoint = this.rtbPartner.endpoint;
  }

  preparePartnerObject(data): void {
    this.rtbPartner = _.cloneDeep(data);
    this.rtbPartner.endpoint = this.rtbPartner.us_west_endpoint || this.rtbPartner.us_east_endpoint;
    this.rtbPartnerEndpointAdvancedMode = this.rtbPartner.us_west_endpoint != this.rtbPartner.us_east_endpoint;
    this.updateConfigJSON();

    if (this.rtbPartner.provider === undefined && this.rtbPartner.environment === undefined) {
      this.detectProvider();
    }
  }

  updateConfigJSON(data?) {
    const config = data || this.rtbPartner.config_json;

    if (this.sharedService.isJSON(config)) {
      this.rtbPartner.config_json = stringify(JSON.parse(config), {
        space: this.sharedService.formatJSONstring,
      });
    } else {
      this.rtbPartner.config_json = '';
    }
  }

  onSave(): void {
    const noErrors: boolean = this.validatePartner();

    if (noErrors) {
      this.isLoading = true;
      if (!this.rtbPartnerEndpointAdvancedMode) {
        this.rtbPartner.us_west_endpoint = this.rtbPartner.endpoint;
        this.rtbPartner.us_east_endpoint = this.rtbPartner.endpoint;
      }
      // Force fields that are marked numbers to real Numbers and not Strings
      this.rtbPartner.config_json = JSON.parse(this.rtbPartner.config_json);
      this.providerFields.value.forEach(field => {
        const { name, type, value, ignoreAssignment, path } = field;
        let processedValue = value;

        if (name === 'authorization' && value) {
          const trimmedValue = value.trim();
          if (!trimmedValue.toLowerCase().startsWith('basic ')) {
            processedValue = `Basic ${trimmedValue}`;
          } else {
            processedValue = trimmedValue;
          }
        }
        if (!ignoreAssignment && !path) {
          this.rtbPartner.config_json[name] = type === 'number' ? Math.max(1, Number(processedValue) || 0) : processedValue;
        }
      });
      this.rtbPartner.config_json = JSON.stringify(this.rtbPartner.config_json);

      this.rtbPartner.config_json = this.sharedService.isJSON(this.rtbPartner.config_json)
        ? stringify(JSON.parse(this.rtbPartner.config_json))
        : '';
      this.savePartner(this.rtbPartner).subscribe(
        (response) => {
          this.isLoading = false;
          if (response && response.success && +response.data.id > 0) {
            if (this.editMode) {
              this.preparePartnerObject(response.data);
              this.sharedService.showFlashMessage(
                this.dictionaryService.get('RTB_PARTNER_SAVED_SUCCESSFULLY'),
                'success',
              );
            } else {
              this.sharedService.showFlashMessage(
                this.dictionaryService.get('RTB_PARTNER_CREATED_SUCCESSFULLY'),
                'success',
              );
              this.router.navigate([`${this.backLink}/${response.data.id}`]).then(() => {
                return;
              });
            }
          } else {
            if (response.errors) {
              this.errorInputs = Object.keys(response.errors);

              if (this.errorInputs.length) {
                _.forEach(this.errorInputs, (one) => {
                  this.sharedService.showFlashMessage(response.errors[one], 'error');
                });
              } else {
                this.sharedService.showFlashMessage(this.dictionaryService.get('AN_ERROR_HAS_OCCURRED'), 'error');
              }
            } else {
              this.sharedService.showFlashMessage(this.dictionaryService.get('AN_ERROR_HAS_OCCURRED'), 'error');
            }
          }
        },
        (error: any) => {
          this.isLoading = false;
          this.sharedService.showFlashMessage(this.dictionaryService.get('AN_ERROR_HAS_OCCURRED'), 'error');
        },
      );
    }
  }

  isSectionActive(id: number) {
    let answer = true;
    const isAdminOnly: boolean = this.allSections[id]['isAdminOnly'];

    if (isAdminOnly) {
      answer = answer && this.userService.isAdmin();
    }

    return answer;
  }

  cancel(): void {
    this.router.navigate([this.backLink]).then(() => {
      return;
    });
  }

  formatConfigJSON() {
    if (this.validateExtraParamsJSON()) {
      setTimeout(() => {
        this.rtbPartner.config_json = stringify(JSON.parse(this.rtbPartner.config_json), {
          space: this.sharedService.formatJSONstring,
        });
        this.updateForm();
      });
    }
  }

  validateExtraParamsJSON(): boolean {
    this.errorInputs = _.without(this.errorInputs, 'config_json');

    if (this.rtbPartner.config_json.length && !this.sharedService.isJSON(this.rtbPartner.config_json)) {
      this.errorInputs.push('config_json');
    }

    this.errorInputs = _.uniq(this.errorInputs);
    return this.sharedService.isJSON(this.rtbPartner.config_json);
  }

  onError(isNotFound: boolean = false): void {
    const message: string = isNotFound ? 'RTB_PARTNER_NOT_FOUND' : 'AN_ERROR_HAS_OCCURRED';

    this.isLoading = false;
    this.router.navigate([this.backLink]).then(() => {
      return;
    });

    this.sharedService.showFlashMessage(this.dictionaryService.get(message), 'error');
  }

  getSectionsPosition(): number {
    return window.scrollY > 72 ? 0 : 72;
  }

  getSectionsArrowPosition(): number {
    return this.activeSection && document.getElementById('sectionButton' + this.activeSection)
      ? document.getElementById('sectionButton' + this.activeSection).offsetTop
      : 0;
  }

  goToSection(id: number): void {
    const section = document.getElementById(this.allSections[id].value);
    const yPosition = id > 0 ? section.offsetTop : 0;
    window.scroll({ top: yPosition, left: 0, behavior: 'smooth' });
    this.activeSection = id;
    if (!this.allSections[id].toggleStatus) {
      this.toggleSection(id);
    }
    setTimeout(() => {
      this.activeSection = id;
    }, 400);
  }

  toggleSection(id: number): void {
    this.allSections[id].toggleStatus = !this.allSections[id].toggleStatus;
  }

  validatePartner(): boolean {
    let valid = true;
    const invalidProviderFields = this.getInvalidProviderFields();
    this.errorInputs = [];

    if (invalidProviderFields.length) {
      valid = false;
      this.errorInputs.push(...invalidProviderFields);
    }

    if (this.sharedService.isEmpty(this.rtbPartner.provider)) {
      valid = false;
      this.errorInputs.push('provider');
    }

    if (
      !this.sharedService.isEmpty(this.rtbPartner.provider) &&
      this.rtbPartner.provider !== 0 &&
      this.sharedService.isEmpty(this.rtbPartner.environment)
    ) {
      valid = false;
      this.errorInputs.push('environment');
    }

    if (this.sharedService.isEmpty(this.rtbPartner.name.trim())) {
      valid = false;
      this.errorInputs.push('name');
    }

    this.errorInputs = _.uniq(this.errorInputs);
    if (!valid) {
      this.sharedService.showFlashMessage(this.dictionaryService.get('NEW_ENTITY_GENERAL_ERROR'), 'error');
    }
    return valid;
  }

  getPartner(): Observable<any> {
    return this.httpService.post('vdsp_ws/getRtbPartner', {
      id: this.routeParams.id,
    });
  }

  savePartner(params: any): Observable<any> {
    const configJson = JSON.parse(params.config_json);
    const providerConfig = this.getProviderConfig(params.provider);
    if (this.userService.isAdmin() && (providerConfig && providerConfig.admin && providerConfig.admin.config)) {
      params.config_json = JSON.stringify({ ...configJson, ...providerConfig.admin.config });
    }
    return this.httpService.post('rtb_partners_manager/save', params);
  }

  regExpReader(source: string, pattern: string): string | null {
    const regex = new RegExp(pattern);
    const matched = source.match(regex);

    if (matched && matched[1]) {
      return matched[1];
    }

    return null;
  }

  queryParamsReader(source: string, param: string): string | null {
    const url = new URL(source);

    return url.searchParams.get(param);
  }

  protected readonly WEB_ENV = WEB_ENV;
}
